import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { firstInitial } from "@/lib/trainerIdentity";
import type { Friend } from "@/lib/friends";

type LiveFriendRow = {
  id: number;
  friend: {
    id: number;
    username: string | null;
    displayName: string | null;
    trainerName: string | null;
  } | null;
};

/**
 * Resolve a friend's display identity from their ACCOUNT id.
 *
 * A numeric route id comes from a real connected trainer (the LiveFriendsSection
 * rows), which the seeded demo `findFriend()` can't resolve — so every per-friend
 * page pulls the trainer's display fields from /api/friends. Set maths comes
 * from elsewhere; this is only who the page is about.
 *
 * Returns null while the list is loading or when the id isn't a friend, so the
 * caller can tell "not found" apart from "not loaded yet" via `isLoading`.
 */
export function useFriendIdentity(accountId: number | null): {
  friend: Friend | null;
  isLoading: boolean;
} {
  const q = useQuery<{ friends: LiveFriendRow[] }>({
    queryKey: ["/api/friends"],
    enabled: accountId != null,
    staleTime: 60_000,
  });

  const friend = useMemo(() => {
    if (accountId == null) return null;
    const row = q.data?.friends.find((r) => r.friend?.id === accountId);
    const fr = row?.friend;
    if (!fr) return null;
    // PoGo identity first — never the IdP displayName as the headline.
    const name = fr.trainerName || fr.displayName || fr.username || "Trainer";
    const identity: Friend = {
      id: String(accountId),
      handle: fr.username ? `@${fr.username}` : "Connected trainer",
      displayName: name,
      trainerCode: "",
      monogram: firstInitial(name, "T"),
      // Deterministic hue from the account id so the avatar colour is stable
      // across renders (and across pages) without needing a stored value.
      hue: (accountId * 47) % 360,
      lastActive: "",
      progress: { main: 0, shiny: 0, lucky: 0, perfect: 0 },
    };
    return identity;
  }, [accountId, q.data]);

  return { friend, isLoading: q.isLoading };
}

/** One friend, as a picker needs them: who they are and how to draw them. */
export interface FriendChoice {
  accountId: number;
  name: string;
  monogram: string;
  hue: number;
}

/**
 * Every connected friend, for the "which friend?" question.
 *
 * Compare and Trade are both per-friend pages, so a menu entry for either has
 * to ask WHO before it can go anywhere. This is that list, derived exactly the
 * way useFriendIdentity derives a single one — same query key, same name
 * precedence, same deterministic hue — so the avatar a trainer picks here is
 * the avatar they land on.
 */
export function useFriendChoices(): { friends: FriendChoice[]; isLoading: boolean } {
  const q = useQuery<{ friends: LiveFriendRow[] }>({
    queryKey: ["/api/friends"],
    staleTime: 60_000,
  });

  const friends = useMemo(() => {
    const rows = q.data?.friends ?? [];
    return rows
      .map((r) => r.friend)
      .filter((f): f is NonNullable<LiveFriendRow["friend"]> => f != null)
      .map((f) => {
        // PoGo identity first — never the IdP displayName as the headline.
        const name = f.trainerName || f.displayName || f.username || "Trainer";
        return {
          accountId: f.id,
          name,
          monogram: firstInitial(name, "T"),
          hue: (f.id * 47) % 360,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [q.data]);

  return { friends, isLoading: q.isLoading };
}
